import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = "https://newalemny.3almny.app/api";

// Fetch teacher profile
export const fetchTeacherProfile = createAsyncThunk(
  "teacherDetails/fetchTeacherProfile",
  async (teacherId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${BASE_URL}/teacher/teacher_profile/${teacherId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في جلب بيانات المدرس");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في جلب بيانات المدرس");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

// Fetch teacher lessons
export const fetchTeacherLessons = createAsyncThunk(
  "teacherDetails/fetchTeacherLessons",
  async ({ teacherId, page = 1, perPage = 15 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${BASE_URL}/teacher/teacher_lessons/${teacherId}?page=${page}&per_page=${perPage}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في جلب دروس المدرس");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في جلب دروس المدرس");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

// Fetch teacher ratings
export const fetchTeacherRatings = createAsyncThunk(
  "teacherDetails/fetchTeacherRatings",
  async (teacherId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${BASE_URL}/teacher/teacher_rates/${teacherId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في جلب تقييمات المدرس");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في جلب تقييمات المدرس");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

// Submit teacher rating
export const submitTeacherRating = createAsyncThunk(
  "teacherDetails/submitTeacherRating",
  async (ratingData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.post(`${BASE_URL}/teacher/teacher_rate`, ratingData, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في إرسال التقييم");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في إرسال التقييم");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

const teacherDetailsSlice = createSlice({
  name: "teacherDetails",
  initialState: {
    teacherProfile: null,
    teacherLessons: [],
    teacherRatings: [],
    loading: false,
    profileLoading: false,
    lessonsLoading: false,
    ratingsLoading: false,
    submitRatingLoading: false,
    error: null,
    profileError: null,
    lessonsError: null,
    ratingsError: null,
    submitRatingError: null,
    lessonsPagination: null,
    currentLessonsPage: 1,
  },
  reducers: {
    clearTeacherDetails: (state) => {
      state.teacherProfile = null;
      state.teacherLessons = [];
      state.teacherRatings = [];
      state.loading = false;
      state.profileLoading = false;
      state.lessonsLoading = false;
      state.ratingsLoading = false;
      state.submitRatingLoading = false;
      state.error = null;
      state.profileError = null;
      state.lessonsError = null;
      state.ratingsError = null;
      state.submitRatingError = null;
      state.lessonsPagination = null;
      state.currentLessonsPage = 1;
    },
    setCurrentLessonsPage: (state, action) => {
      state.currentLessonsPage = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
      state.profileError = null;
      state.lessonsError = null;
      state.ratingsError = null;
      state.submitRatingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch teacher profile
      .addCase(fetchTeacherProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchTeacherProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.teacherProfile = action.payload;
      })
      .addCase(fetchTeacherProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })
      // Fetch teacher lessons
      .addCase(fetchTeacherLessons.pending, (state) => {
        state.lessonsLoading = true;
        state.lessonsError = null;
      })
      .addCase(fetchTeacherLessons.fulfilled, (state, action) => {
        state.lessonsLoading = false;
        state.teacherLessons = action.payload.data || [];
        state.lessonsPagination = action.payload.pagination || null;
      })
      .addCase(fetchTeacherLessons.rejected, (state, action) => {
        state.lessonsLoading = false;
        state.lessonsError = action.payload;
      })
      // Fetch teacher ratings
      .addCase(fetchTeacherRatings.pending, (state) => {
        state.ratingsLoading = true;
        state.ratingsError = null;
      })
      .addCase(fetchTeacherRatings.fulfilled, (state, action) => {
        state.ratingsLoading = false;
        state.teacherRatings = action.payload.data || [];
      })
      .addCase(fetchTeacherRatings.rejected, (state, action) => {
        state.ratingsLoading = false;
        state.ratingsError = action.payload;
      })
      // Submit teacher rating
      .addCase(submitTeacherRating.pending, (state) => {
        state.submitRatingLoading = true;
        state.submitRatingError = null;
      })
      .addCase(submitTeacherRating.fulfilled, (state, action) => {
        state.submitRatingLoading = false;
        // Refresh ratings after successful submission
        // The component should refetch ratings
      })
      .addCase(submitTeacherRating.rejected, (state, action) => {
        state.submitRatingLoading = false;
        state.submitRatingError = action.payload;
      });
  },
});

export const { clearTeacherDetails, setCurrentLessonsPage, clearErrors } = teacherDetailsSlice.actions;
export default teacherDetailsSlice.reducer; 